import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_FLOW_RUN_LOGS_REQUEST,
  GET_FLOW_RUN_LOGS_METADATA_REQUEST,
  GET_FLOW_RUN_LOGS_WITH_FACETS_REQUEST_V2,
  GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_REQUEST_V2,
} from 'js/constants/actionTypes';
import { defineds } from 'js/components/elements/datepicker/defaultRanges';
import { PaginatedLog, UseFlowRunsLogsResponseType, DateRange } from '../types';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { buildOptionsForV1 } from 'js/components/data_flows/cards/FlowRunLogs/utils/buildOptions';
import { PAGINATION, TIMING, SortOrder, SORT_ORDER } from '../constants';
import { useSmartPolling } from 'new/hooks/useSmartPolling';

import { useFilter } from './useFilter';
import { useAdapter } from './useAdapter';
import { useRepository } from './useRepository';
import { usePagination } from './usePagination';
import { Options as FetchFlowRunLogsWithFacetsOptions } from 'js/actions/metrics/flowRunLogsWithFacets';
import { calculateInitialDateRange } from '../utils/calculateInitialDateRange';

type UseFlowRunsLogsParams = {
  originNodeId: string;
  runId: string;
  exploreFlowLogs?: boolean;
  sortOrder?: SortOrder;
  viewSortOrder?: SortOrder;
  createdAt?: string | null;
  autoRefreshIntervalValue?: number;
  skipClearLogsOnUnmount?: boolean;
};

export const useFlowRunsLogs = (params: UseFlowRunsLogsParams): UseFlowRunsLogsResponseType => {
  const {
    originNodeId,
    runId,
    exploreFlowLogs,
    sortOrder = SORT_ORDER.NEWEST,
    viewSortOrder = SORT_ORDER.NEWEST,
    createdAt,
    autoRefreshIntervalValue,
    skipClearLogsOnUnmount,
  } = params;

  const isFlowInsightsV2Enabled = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');

  const runStartDate = calculateInitialDateRange(runId, createdAt);
  const [error, setError] = useState<Error | null>(null);
  const initialDateRange: DateRange = {
    from: runStartDate,
    to: defineds.endOfToday.getTime(),
  };
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const [startDate, onDateChange] = useState(runStartDate);
  const [isPageSetupCompleted, setIsPageSetupCompleted] = useState(false);
  const [showInitialLoader, setShowInitialLoader] = useState(true);

  const isLoadingRef = useRef(false);
  const lastRequestTimestamp = useRef(0);
  const isComponentMountedRef = useRef(false);
  const runIdRef = useRef(runId);
  const dateRangeRef = useRef(dateRange);
  const startDateRef = useRef(startDate);
  const severityForV1Ref = useRef('');
  const severityForV2Ref = useRef([] as string[]);
  const exploreFlowLogsRef = useRef(exploreFlowLogs);
  const sortOrderRef = useRef<SortOrder>(sortOrder);
  const initialLoadStartTime = useRef<number | null>(null);
  const initialLoaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const pagination = usePagination();
  const actions = useAdapter(originNodeId);
  const repository = useRepository(originNodeId);
  const logsFilter = useFilter();
  const isFetchingLogs = useIsLoading(
    GET_FLOW_RUN_LOGS_REQUEST,
    GET_FLOW_RUN_LOGS_METADATA_REQUEST,
    GET_FLOW_RUN_LOGS_WITH_FACETS_REQUEST_V2,
    GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_REQUEST_V2,
  );

  const logsWithFacetsByFlowId = useMemo(() => {
    return repository.getFlowRunLogsWithFacetsByFlowId(viewSortOrder); // get the logs with the view sort order from redux store
  }, [repository, viewSortOrder]);

  const { filterBy } = logsFilter;
  const {
    clearLogs,
    fetchFlowRunLogsPage,
    fetchFlowRunLogsWithFacets,
    fetchFlowRunLogsWithFacetsAutoRefresh,
  } = actions;

  const {
    getFlowRuns,
    getFlowRunLogsWithFacets,
    getFlowRunLogsNextPageValues,
    getFlowRunLogsPagedByFlowId,
  } = repository;

  useEffect(() => {
    /**
     * @WHY?
     * - Sync refs with the latest values, this is needed to avoid race conditions when the values change.
     */
    runIdRef.current = runId;
    dateRangeRef.current = dateRange;
    startDateRef.current = startDate;
    severityForV1Ref.current = filterBy.severity;
    severityForV2Ref.current = filterBy.severities;
    exploreFlowLogsRef.current = exploreFlowLogs;
    sortOrderRef.current = sortOrder;
  }, [
    runId,
    dateRange,
    startDate,
    exploreFlowLogs,
    filterBy.severity,
    filterBy.severities,
    sortOrder,
  ]);

  const buildOptionsV2 = useCallback(
    (
      dateRangeParam?: DateRange | number,
      searchAfterValues?: { log_id: string; timestamp: number } | 'initial_request',
      severity?: string[],
      sortOrder?: SortOrder,
    ): FetchFlowRunLogsWithFacetsOptions => {
      let fromToUse: number;
      let toToUse: number;

      if (dateRangeParam) {
        if (typeof dateRangeParam === 'number') {
          fromToUse = dateRangeParam;
          toToUse = defineds.endOfToday.getTime();
        } else {
          fromToUse = dateRangeParam.from;
          toToUse = dateRangeParam.to;
        }
      } else {
        fromToUse = dateRangeRef.current.from;
        toToUse = dateRangeRef.current.to;
      }

      const runIdToUse = runIdRef.current;
      const severityToUse = severity ?? severityForV2Ref.current;
      const sortOrderToUse = sortOrder ?? sortOrderRef.current;

      const params: FetchFlowRunLogsWithFacetsOptions['params'] = {
        facets: true,
        sort: sortOrderToUse,
        size: PAGINATION.PER_PAGE,
        from: fromToUse,
        to: toToUse,
      };

      if (exploreFlowLogsRef.current && (!params.from || !params.to)) {
        delete params.to;
        delete params.from;
      }

      type PaginationType = {
        search_after_values: {
          timestamp: number;
          log_id: string;
        };
      };

      let pagination: PaginationType | undefined;

      if (searchAfterValues === 'initial_request' || !searchAfterValues) {
        pagination = undefined;
      } else {
        pagination = {
          search_after_values: searchAfterValues as any,
        };
      }

      return {
        params,
        body: {
          pagination: { ...pagination },
          severity: severityToUse,
          run_ids: runIdToUse ? [runIdToUse] : [],
        },
      } as any;
    },
    [],
  );

  /**
   * Initializes flow run logs by loading page 1 for immediate display.
   *
   * Loading Strategy:
   * 1. Fetches page 1 to retrieve logs and metadata (total page count)
   * 2. Sets pagination state based on total pages
   */
  const setupFlowRunLogsV1 = useCallback(
    async (dateRangeParam?: DateRange | number, explicitRunId?: string) => {
      isLoadingRef.current = false;
      lastRequestTimestamp.current = 0;

      let fromToUse: number;
      let toToUse: number;

      if (dateRangeParam) {
        if (typeof dateRangeParam === 'number') {
          fromToUse = dateRangeParam;
          toToUse = defineds.endOfToday.getTime();
        } else {
          fromToUse = dateRangeParam.from;
          toToUse = dateRangeParam.to;
        }
      } else {
        fromToUse = dateRangeRef.current.from;
        toToUse = dateRangeRef.current.to;
      }

      const runIdToUse = explicitRunId ?? runIdRef.current;
      const severityForV1ToUse = severityForV1Ref.current;

      const initialFetchOptions = {
        page: 1,
        from: fromToUse,
        runId: runIdToUse || '0',
        to: toToUse,
        severity: severityForV1ToUse,
      };

      const resp = await fetchFlowRunLogsPage(
        buildOptionsForV1(initialFetchOptions, exploreFlowLogsRef.current, sortOrderRef.current),
      );

      const { meta } = resp.data.logs ?? {};

      if (!meta || typeof meta.pages_count !== 'number') {
        const errorMessage = `Invalid response structure: missing or invalid meta.pages_count. Request params: runId=${initialFetchOptions.runId}`;
        console.error('setupFlowRunLogsV1:', errorMessage);
        setError(new Error(errorMessage));
        return;
      }

      const totalOfPages = meta.pages_count;
      const hasMorePageToFetch = totalOfPages > 1;

      pagination.onChangeMinPage(1);
      pagination.onChangeMaxPage(totalOfPages);
      pagination.onChangeHasMore(hasMorePageToFetch);
      pagination.onChangeNextPage(hasMorePageToFetch ? 2 : 0);
    },
    [pagination],
  );

  const executeFlowRunLogsWithFacetsFetch = useCallback(
    async (nextPageValues?: any) => {
      const dateRangeToUse = dateRangeRef.current;
      const severityForV2ToUse = severityForV2Ref.current;

      const optionsV2 = buildOptionsV2(dateRangeToUse, nextPageValues, severityForV2ToUse);

      const res = await fetchFlowRunLogsWithFacets(optionsV2);

      if (res.error) {
        setError(new Error(res.error));
        return;
      }

      setError(null);
    },
    [fetchFlowRunLogsWithFacets],
  );

  /**
   * Validate if the request is valid and execute the fetch
   * @returns void
   */
  const requestFlowRunLogsWithFacets = useCallback(async () => {
    const currentNextPageValues = repository.getFlowRunLogsNextPageValues();
    const isFirstRequest = currentNextPageValues === 'initial_request';

    if ((!currentNextPageValues && !isFirstRequest) || isFetchingLogs) return;

    await executeFlowRunLogsWithFacetsFetch(currentNextPageValues);
  }, [repository, isFetchingLogs, executeFlowRunLogsWithFacetsFetch]);

  /**
   * Request the next page of logs for the V1 implementation
   * @returns void
   */
  const requestFlowRunLogsV1Page = useCallback(async () => {
    const nextPage = pagination.nextPage;

    if (nextPage <= 0 || isFetchingLogs || isLoadingRef.current) return;

    if (nextPage > pagination.maxPage) {
      pagination.onChangeHasMore(false);
      return;
    }

    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimestamp.current;

    if (timeSinceLastRequest < TIMING.THROTTLE_DELAY) return;

    isLoadingRef.current = true;
    lastRequestTimestamp.current = now;

    try {
      const runIdToUse = runIdRef.current || '0';
      const severityForV1ToUse = severityForV1Ref.current;
      const dateRangeToUse = dateRangeRef.current;

      const optionsParams = {
        page: nextPage,
        from: dateRangeToUse.from,
        runId: runIdToUse,
        to: dateRangeToUse.to,
        severity: severityForV1ToUse,
      };

      const options = buildOptionsForV1(
        optionsParams,
        exploreFlowLogsRef.current,
        sortOrderRef.current,
      );
      await fetchFlowRunLogsPage(options);

      const newNextPage = nextPage + 1;
      pagination.onChangeNextPage(newNextPage);
      if (newNextPage > pagination.maxPage) {
        pagination.onChangeHasMore(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [pagination.nextPage, pagination.maxPage, fetchFlowRunLogsPage, isFetchingLogs]);

  /**
   * Debounce the refetch of logs on filter change
   * @returns void
   * @description
   * - Clear the logs
   * - Execute the fetch for the V1 implementation or V2 implementation
   * - Cancel the debounce
   */
  const debouncedRefetchLogsOnFilterChange = useMemo(
    () =>
      debounce(async () => {
        // Guard: Don't proceed if component has unmounted
        if (!isComponentMountedRef.current) return;

        if (isLoadingRef.current) return;

        isLoadingRef.current = true;
        lastRequestTimestamp.current = 0;

        try {
          await actions.clearLogs();

          if (isFlowInsightsV2Enabled) {
            await executeFlowRunLogsWithFacetsFetch('initial_request');
          } else {
            await setupFlowRunLogsV1();
          }
        } catch (error) {
          console.error('Error fetching logs after filter change:', error);
        } finally {
          isLoadingRef.current = false;
        }
      }, TIMING.FILTER_DEBOUNCE_DELAY),
    [actions, isFlowInsightsV2Enabled, requestFlowRunLogsWithFacets, setupFlowRunLogsV1],
  );

  /**
   * Setup Hook - Initial Load
   */
  useEffect(() => {
    isLoadingRef.current = false;
    lastRequestTimestamp.current = 0;

    if (isComponentMountedRef.current === false) {
      initialLoadStartTime.current = Date.now();

      const requestFlowRunLogsFn = isFlowInsightsV2Enabled
        ? requestFlowRunLogsWithFacets
        : setupFlowRunLogsV1;

      (async () =>
        await requestFlowRunLogsFn()
          .catch(setError)
          .finally(() => {
            setIsPageSetupCompleted(true);
            isComponentMountedRef.current = true;

            const elapsed = Date.now() - (initialLoadStartTime.current || Date.now());
            const remainingDelay = Math.max(0, TIMING.INITIAL_LOADER_MIN_DELAY - elapsed);

            initialLoaderTimeoutRef.current = setTimeout(() => {
              if (isComponentMountedRef.current) {
                setShowInitialLoader(false);
              }
            }, remainingDelay);
          }))();
    }

    return () => {
      isComponentMountedRef.current = false;

      if (initialLoaderTimeoutRef.current) {
        clearTimeout(initialLoaderTimeoutRef.current);
        initialLoaderTimeoutRef.current = null;
      }
      isLoadingRef.current = false;
      lastRequestTimestamp.current = 0;
      if (!skipClearLogsOnUnmount) {
        actions.clearLogs().catch(console.error);
      }
    };
  }, [skipClearLogsOnUnmount]);

  useEffect(() => {
    if (!isComponentMountedRef.current) return;

    debouncedRefetchLogsOnFilterChange();

    return () => {
      debouncedRefetchLogsOnFilterChange.cancel();
    };
  }, [filterBy.severity, filterBy.severities, sortOrder]);

  const hasMoreLogsWithFacets = useMemo(() => {
    return getFlowRunLogsWithFacets()?.hasMore ?? true;
  }, [getFlowRunLogsWithFacets]);

  /**
   * Auto-refresh callback for smart polling
   * Only refreshes when user is at the bottom of the list
   * Handles both V1 and V2 implementations
   * - For V2:
   *   - NEWEST (desc): always refreshes (new logs appear at the beginning)
   *   - OLDEST (asc): only refreshes when all pages are loaded (new logs appear at the end)
   * - For V1:
   *   - NEWEST (desc): always refreshes page 1 where new logs appear
   *   - OLDEST (asc): only refreshes when user is on the last page (where new logs appear)
   */
  const autoRefreshCallback = useCallback(async () => {
    if (!isComponentMountedRef.current) return;
    if (isLoadingRef.current) return;

    if (isFlowInsightsV2Enabled) {
      const sortOrderCurrent = sortOrderRef.current;
      const nextPageValues = getFlowRunLogsNextPageValues();
      const hasMore = hasMoreLogsWithFacets;

      let shouldAutoRefresh = false;

      if (sortOrderCurrent === SORT_ORDER.NEWEST) {
        shouldAutoRefresh = true;
      } else if (sortOrderCurrent === SORT_ORDER.OLDEST) {
        const allPagesLoaded = nextPageValues === null || !hasMore;
        if (allPagesLoaded) {
          shouldAutoRefresh = true;
        }
      }

      if (!shouldAutoRefresh) return;

      /**
       * Build the options for the auto-refresh request
       * - If the sort order is OLDEST, we need to pass the next page values
       * - If the sort order is NEWEST, we don't need to pass anything, first page is always the newest logs
       */
      let createdOptions = buildOptionsV2();
      if (sortOrderCurrent === SORT_ORDER.OLDEST) {
        createdOptions = buildOptionsV2(undefined, nextPageValues);
      }

      await fetchFlowRunLogsWithFacetsAutoRefresh(createdOptions);
    } else {
      const sortOrderCurrent = sortOrderRef.current;
      const maxPage = pagination.maxPage;
      const nextPage = pagination.nextPage;
      const hasMore = pagination.hasMore;

      // Determine if auto-refresh should run and which page to fetch
      let shouldAutoRefresh = false;
      let pageToRefresh = 1;

      if (sortOrderCurrent === SORT_ORDER.NEWEST) {
        // NEWEST (desc): new logs always appear on page 1
        shouldAutoRefresh = true;
        pageToRefresh = 1;
      } else if (sortOrderCurrent === SORT_ORDER.OLDEST) {
        // OLDEST (asc): new logs appear on the last page
        // Only auto-refresh if user is viewing the last page
        const isOnLastPage = nextPage > maxPage || !hasMore;
        if (isOnLastPage) {
          shouldAutoRefresh = true;
          pageToRefresh = maxPage;
        }
      }

      if (!shouldAutoRefresh) return;

      const runIdToUse = runIdRef.current || '0';
      const severityForV1ToUse = severityForV1Ref.current;
      const dateRangeToUse = dateRangeRef.current;

      const options = buildOptionsForV1(
        {
          page: pageToRefresh,
          from: dateRangeToUse.from,
          runId: runIdToUse,
          to: dateRangeToUse.to,
          severity: severityForV1ToUse,
        },
        exploreFlowLogsRef.current,
        sortOrderRef.current,
      );

      const handledResponseApi = await fetchFlowRunLogsPage(options);

      if (handledResponseApi.error) {
        setError(new Error(handledResponseApi.error));
        return;
      }

      const meta = handledResponseApi.data.logs.meta;
      pagination.onChangeMaxPage(meta.pages_count);
    }
  }, [
    hasMoreLogsWithFacets,
    isFlowInsightsV2Enabled,
    buildOptionsV2,
    fetchFlowRunLogsWithFacetsAutoRefresh,
    fetchFlowRunLogsPage,
    getFlowRunLogsNextPageValues,
    pagination.maxPage,
    pagination.nextPage,
    pagination.hasMore,
    pagination.onChangeMaxPage,
  ]);

  // Use smart polling for auto-refresh with visibility and idle awareness
  // Uses FLOW_LOGS preset (10s) for real-time log viewing
  // Falls back to autoRefreshIntervalValue if provided, otherwise uses preset config
  useSmartPolling({
    callback: autoRefreshCallback,
    preset: 'FLOW_LOGS',
    intervalOverride: autoRefreshIntervalValue,
    enabled: isComponentMountedRef.current !== false,
  });

  const onRefresh = useCallback(async () => {
    setError(null);
    isLoadingRef.current = false;
    lastRequestTimestamp.current = 0;

    await actions.clearLogs();
    if (isFlowInsightsV2Enabled) {
      await fetchFlowRunLogsWithFacets(buildOptionsV2());
    } else {
      await setupFlowRunLogsV1();
    }
  }, [actions, isFlowInsightsV2Enabled, buildOptionsV2, setupFlowRunLogsV1]);

  /**
   * Helper function to load more logs based on the current version (V1 or V2)
   */
  const loadLogs = useCallback(async () => {
    if (error) return;

    const fn = isFlowInsightsV2Enabled ? requestFlowRunLogsWithFacets : requestFlowRunLogsV1Page;

    await fn();
  }, [error, isFlowInsightsV2Enabled, requestFlowRunLogsV1Page, requestFlowRunLogsWithFacets]);

  const loadMoreLogs = useCallback(async () => {
    await loadLogs();
  }, [loadLogs]);

  const onStartDateChanged = useCallback(
    async (newValue: number | DateRange, newRunId?: string) => {
      if (isLoadingRef.current) return;

      isLoadingRef.current = true;

      try {
        lastRequestTimestamp.current = 0;

        await clearLogs();

        let newDateRange: DateRange;
        if (typeof newValue === 'number') {
          newDateRange = {
            from: newValue,
            to: defineds.endOfToday.getTime(),
          };
        } else {
          newDateRange = newValue;
        }

        if (isFlowInsightsV2Enabled) {
          await fetchFlowRunLogsWithFacets(buildOptionsV2(newDateRange));
        } else {
          await setupFlowRunLogsV1(newDateRange, newRunId);
        }

        setDateRange(newDateRange);
        onDateChange(newDateRange.from);
      } finally {
        isLoadingRef.current = false;
      }
    },
    [buildOptionsV2, setupFlowRunLogsV1, fetchFlowRunLogsWithFacets, clearLogs],
  );

  const logsPagedByFlowId = getFlowRunLogsPagedByFlowId(runId, pagination.nextPage, viewSortOrder);

  const logs = useMemo(() => {
    const _logs = isFlowInsightsV2Enabled
      ? logsWithFacetsByFlowId
      : (logsPagedByFlowId.data as PaginatedLog[]);

    if (runId && runId !== '0') {
      return {
        data: { [runId]: _logs },
        meta: logsPagedByFlowId.meta,
      };
    }

    return {
      data: {
        historical: _logs,
      },
      meta: logsPagedByFlowId.meta,
    };
  }, [isFlowInsightsV2Enabled, logsWithFacetsByFlowId, logsPagedByFlowId, runId]);

  const isFirstPageRendered = pagination.nextPage === 0;

  const hasMore = isFlowInsightsV2Enabled
    ? (getFlowRunLogsWithFacets()?.hasMore ?? true)
    : pagination.hasMore && pagination.nextPage > 0;

  return {
    hasError: !!error,
    isFetchingLogs,
    isPageSetupCompleted,
    showInitialLoader,
    logs,
    filter: logsFilter,
    runs: { data: getFlowRuns() ?? {} },
    date: {
      startDate,
      dateRange,
      onDateChange,
      onStartDateChanged: onStartDateChanged,
    },
    paging: {
      isFirstPageRendered,
      hasMore,
    },
    actions: {
      onRefresh,
      loadMoreLogs,
      clearLogs: actions.clearLogs,
    },
    repository,
  };
};
